/**
 * shpflt-preventa-guard.js
 *
 * Cart-layer enforcement of pre-order (preventa) per-variant limits.
 *
 * Wraps window.fetch and blocks POSTs to /cart/add, /cart/change and /cart/update
 * that would take a limited variant past its remaining allowance. The allowance is
 * exposed to the page as a { variantId: remaining } map, read from two sources:
 *   - window.__preventaCaps
 *   - <script type="application/json" data-preventa-caps-map> elements — these are
 *     read at action time, so they also work inside AJAX-injected markup (e.g. the
 *     quick-add drawer), where inline <script> tags do not execute.
 * "remaining" is the per-variant maximum minus the units already sold.
 *
 * This is a storefront convenience guard, not a hard limit. It enforces the normal
 * on-site purchase paths (product page, quick-add, and AJAX cart quantity changes).
 * Requests that never run this script are out of scope and must be enforced elsewhere:
 *   - a checkout permalink or a direct API call;
 *   - a full-page cart update on the cart page (a browser navigation, not a fetch).
 * The pre-order allowance is deliberately independent of Shopify stock levels.
 *
 * Loaded early (deferred module) so window.fetch is patched before any cart action.
 */
(function () {
  if (window.__preventaGuardInstalled) {
    return;
  }
  window.__preventaGuardInstalled = true;
  window.__preventaCaps = window.__preventaCaps || {};

  const originalFetch = window.fetch.bind(window);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  const CHANGE_RE = /\/cart\/(change|update)(\.js)?(\?|$)/;

  // Merge the static window map with any JSON maps present in the DOM (the DOM maps
  // survive AJAX-injected markup such as the quick-add drawer).
  const readCaps = () => {
    const caps = Object.assign({}, window.__preventaCaps || {});
    try {
      document
        .querySelectorAll('script[type="application/json"][data-preventa-caps-map]')
        .forEach((el) => {
          try {
            const map = JSON.parse(el.textContent || '{}');
            Object.keys(map).forEach((key) => {
              caps[key] = map[key];
            });
          } catch (error) {
            /* ignore a malformed map */
          }
        });
    } catch (error) {
      /* querySelectorAll unavailable — fall back to the window map */
    }
    return caps;
  };

  const remainingFor = (id) => {
    const caps = readCaps();
    const key = String(id);
    return Object.prototype.hasOwnProperty.call(caps, key) ? Number(caps[key]) : null;
  };

  const currentCart = async () => {
    try {
      const response = await originalFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      return await response.json();
    } catch (error) {
      return { items: [] };
    }
  };

  const qtyInCart = (cart, id) => {
    let quantity = 0;
    for (const item of cart.items || []) {
      if (String(item.id) === String(id)) {
        quantity += item.quantity;
      }
    }
    return quantity;
  };

  // /cart/change accepts a line key, a variant id, or a 1-based line index as `id`.
  // Resolve whatever was sent to the underlying variant id via the cart.
  const resolveVariantId = (cart, changeId) => {
    const target = String(changeId);
    const items = cart.items || [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (String(item.key) === target || String(item.id) === target || String(i + 1) === target) {
        return item.id;
      }
    }
    return null;
  };

  const limitMessage = (remaining) => {
    const count = Math.max(0, remaining);
    const template =
      (window.themeVariables &&
        window.themeVariables.strings &&
        window.themeVariables.strings.preventaLimit) ||
      'Lo sentimos, solo quedan __COUNT__ uds disponibles en preventa para esta talla.';
    return template.split('__COUNT__').join(count);
  };

  const showToast = (message) => {
    try {
      let container = document.querySelector('.shpflt-preventa-toasts');
      if (!container) {
        container = document.createElement('div');
        container.className = 'shpflt-preventa-toasts';
        document.body.appendChild(container);
      }
      const toast = document.createElement('div');
      toast.className = 'shpflt-preventa-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.textContent = message;
      container.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('is-visible'));
      const remove = () => {
        toast.classList.remove('is-visible');
        window.setTimeout(() => toast.remove(), 250);
      };
      toast.addEventListener('click', remove);
      window.setTimeout(remove, 4000);
    } catch (error) {
      /* if the DOM isn't ready or anything fails, fail silently */
    }
  };

  const notify = (message) => {
    document.dispatchEvent(
      new CustomEvent('preventa:limit', { bubbles: true, detail: { message } })
    );
    showToast(message);
  };

  const blockedResponse = (message) => {
    const payload = JSON.stringify({
      status: 422,
      message,
      description: message,
      preventa_blocked: true
    });
    return new Response(payload, {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  };

  const parseBody = (init, input) => {
    let raw = init && init.body;
    if (raw == null && input && typeof input !== 'string') {
      raw = input.body;
    }
    if (raw == null) {
      return null;
    }
    try {
      if (typeof raw === 'string') {
        return JSON.parse(raw);
      }
      if (raw instanceof URLSearchParams) {
        return Object.fromEntries(raw.entries());
      }
      if (typeof FormData !== 'undefined' && raw instanceof FormData) {
        return Object.fromEntries(raw.entries());
      }
    } catch (error) {
      return null;
    }
    return null;
  };

  window.fetch = async function (input, init) {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

      if (method === 'POST' && ADD_RE.test(url)) {
        const body = parseBody(init, input);
        // /cart/add accepts a single { id, quantity } (product form / quick-add here)
        // or an { items: [...] } batch. Validate every item that has a cap.
        const items = body && Array.isArray(body.items)
          ? body.items
          : body && body.id != null
            ? [{ id: body.id, quantity: body.quantity }]
            : [];

        if (items.length) {
          const cart = await currentCart();
          for (const item of items) {
            const id = item.id;
            const addQty = Number(item.quantity || 1);
            const remaining = id != null ? remainingFor(id) : null;
            if (id != null && remaining != null) {
              const have = qtyInCart(cart, id);
              if (have + addQty > remaining) {
                const allowed = Math.max(0, remaining - have);
                notify(limitMessage(allowed));
                return blockedResponse(limitMessage(allowed));
              }
            }
          }
        }
      }

      if (method === 'POST' && CHANGE_RE.test(url)) {
        const body = parseBody(init, input);
        if (body && body.updates && !Array.isArray(body.updates)) {
          // /cart/update.js → { updates: { variantId: qty } } (keyed by variant id)
          for (const key of Object.keys(body.updates)) {
            const remaining = remainingFor(key);
            if (remaining != null && Number(body.updates[key]) > remaining) {
              notify(limitMessage(remaining));
              return blockedResponse(limitMessage(remaining));
            }
          }
        } else if (body && body.id != null && body.quantity != null) {
          // /cart/change.js → { id: lineKey | variantId | lineIndex, quantity }.
          // quantity is the ABSOLUTE new quantity for that line.
          let remaining = remainingFor(body.id);
          if (remaining == null) {
            const cart = await currentCart();
            const variantId = resolveVariantId(cart, body.id);
            if (variantId != null) {
              remaining = remainingFor(variantId);
            }
          }
          if (remaining != null && Number(body.quantity) > remaining) {
            notify(limitMessage(remaining));
            return blockedResponse(limitMessage(remaining));
          }
        }
      }
    } catch (error) {
      // Never break the cart because of a guard error.
    }

    return originalFetch(input, init);
  };
})();
